import { addMonths, subMonths } from "date-fns";
import { useMemo } from "react";
import { isMonthOutOfRange } from "@/lib/utils/isMonthOutOfRange";

/*
 * Picks when prev/next month controls should be off for a [minYear, maxYear] window.
 * — Parent disabled, or no month picked yet and not in the “empty” placeholder mode: both off.
 * — Placeholder (empty) state: the step uses this month ±1 from today, so an arrow is off if
 *   that target month is outside the window.
 * — Month selected: an arrow is off if moving one month back or forward would land outside
 *   the window.
 */
interface UseMonthNavArrowsDisabledParams {
  minYear: number;
  maxYear: number;
  selectedDate: Date | undefined;
  showEmptyState: boolean;
  disabled?: boolean;
}

interface UseMonthNavArrowsDisabledResult {
  disablePreviousMonth: boolean;
  disableNextMonth: boolean;
}

export function useMonthNavArrowsDisabled({
  minYear,
  maxYear,
  selectedDate,
  showEmptyState,
  disabled,
}: UseMonthNavArrowsDisabledParams): UseMonthNavArrowsDisabledResult {
  return useMemo(() => {
    const minDate = new Date(minYear, 0, 1);
    const maxDate = new Date(maxYear, 11, 31);
    const disableNavButtons =
      (!selectedDate && !showEmptyState) || Boolean(disabled);

    if (disableNavButtons) {
      return { disablePreviousMonth: true, disableNextMonth: true };
    }

    if (showEmptyState) {
      const now = new Date();
      const prevTarget = subMonths(now, 1);
      const nextTarget = addMonths(now, 1);
      return {
        disablePreviousMonth: isMonthOutOfRange(prevTarget, minDate, maxDate),
        disableNextMonth: isMonthOutOfRange(nextTarget, minDate, maxDate),
      };
    }

    if (selectedDate) {
      const d = new Date(selectedDate);
      const prevTarget = subMonths(d, 1);
      const nextTarget = addMonths(d, 1);
      return {
        disablePreviousMonth: isMonthOutOfRange(prevTarget, minDate, maxDate),
        disableNextMonth: isMonthOutOfRange(nextTarget, minDate, maxDate),
      };
    }
    return { disablePreviousMonth: true, disableNextMonth: true };
  }, [selectedDate, showEmptyState, disabled, minYear, maxYear]);
}
